import Phaser from 'phaser';
import { FONT_FAMILY, GAME_WIDTH, getGameHeight } from '../config/constants';
import { PauseOverlay } from '../ui/PauseOverlay';
import { soundManager } from '../audio/SoundManager';
import { LEVEL_COUNT } from '../levels/imageLevels';
import { crispText } from '../ui/textUtils';
import { createGearButton } from '../ui/GearButton';
import { drawGradientBackground } from '../ui/background';
import { enableScrolling } from '../ui/scrollable';
import { createButton } from '../ui/Button';
import { launchGameScene } from '../types/GameSceneLaunchData';
import { fadeToScene } from '../ui/sceneTransition';
import { clearUrlState } from '../utils/urlState';
import { getHighestCompletedLevel, getNextLevel } from '../systems/progressStore';

export class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    // Clear scene URL params when returning to menu
    clearUrlState();

    const centerX = GAME_WIDTH / 2;
    const centerY = getGameHeight() / 2;

    // Check if a GameScene is sleeping (player came from pause menu)
    const hasSleepingGame = this.scene.isSleeping('GameScene');

    // ── Background gradient ──────────────────────────────────────────────
    drawGradientBackground(this);

    // Ambient particles
    this.add.particles(centerX, centerY, 'particle', {
      x: { min: -GAME_WIDTH / 2, max: GAME_WIDTH / 2 },
      y: { min: -getGameHeight() / 2, max: getGameHeight() / 2 },
      speed: { min: 3, max: 10 },
      scale: { start: 0.25, end: 0 },
      alpha: { start: 0.12, end: 0 },
      lifespan: 5000,
      frequency: 200,
      tint: 0x6666aa,
      blendMode: Phaser.BlendModes.ADD,
    });

    // Scrollable container for all content
    const container = this.add.container(0, 0);

    // ── Build ID (big, above title) ──────────────────────────────────────
    const buildStr = `build ${__BUILD_ID__}`;
    const buildPrefix = buildStr.slice(0, -3);
    const buildSuffix = buildStr.slice(-3);

    const buildPrefixText = crispText(this.add.text(0, 0, buildPrefix, {
      fontFamily: FONT_FAMILY,
      fontSize: '52px',
      color: '#aaaacc',
    }), false);
    const buildSuffixText = crispText(this.add.text(0, 0, buildSuffix, {
      fontFamily: FONT_FAMILY,
      fontSize: '52px',
      color: '#ffffff',
      fontStyle: 'bold',
    }), false);

    // Position the two parts side by side, centered
    const totalBuildWidth = buildPrefixText.width + buildSuffixText.width;
    const buildY = centerY - 300;
    buildPrefixText.setPosition(centerX - totalBuildWidth / 2, buildY).setOrigin(0, 0.5).setAlpha(0);
    buildSuffixText.setPosition(centerX - totalBuildWidth / 2 + buildPrefixText.width, buildY).setOrigin(0, 0.5).setAlpha(0);
    container.add(buildPrefixText);
    container.add(buildSuffixText);

    this.tweens.add({
      targets: [buildPrefixText, buildSuffixText],
      alpha: 1,
      y: buildY + 20,
      duration: 600,
      ease: 'Power2',
    });

    // ── Title ────────────────────────────────────────────────────────────
    const title = crispText(this.add.text(centerX, centerY - 220, 'pxFlow', {
      fontFamily: FONT_FAMILY,
      fontSize: '116px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#3a3a6a',
      strokeThickness: 6,
    })).setOrigin(0.5).setAlpha(0);
    container.add(title);

    this.tweens.add({
      targets: title,
      alpha: 1,
      y: centerY - 200,
      duration: 600,
      ease: 'Power2',
    });

    // Subtitle
    container.add(crispText(this.add.text(centerX, centerY - 110, 'action puzzle', {
      fontFamily: FONT_FAMILY,
      fontSize: '32px',
      color: '#7777aa',
    })).setOrigin(0.5));

    // ── Resume button (only if game is sleeping) ───────────────────────
    let nextBtnY = centerY + 60;

    if (hasSleepingGame) {
      createButton(this, { x: centerX, y: nextBtnY, w: 600, h: 112, label: 'RESUME', color: 0x2ecc71, hoverColor: 0x27ae60, container, fontSize: '52px', radius: 24, shadowOffset: 4, shadowAlpha: 0.3, glossInset: 8, glossAlpha: 0.12, onClick: () => {
        fadeToScene(this, () => {
          // Wake the sleeping GameScene and stop MenuScene
          const gameScene = this.scene.get('GameScene');
          this.scene.stop('MenuScene');
          this.scene.wake('GameScene');
          gameScene.cameras.main.fadeIn(200, 0, 0, 0);
        });
      } });
      nextBtnY += 160;
    }

    // ── Play button (continue from saved progress) ─────────────────────
    const savedLevel = getHighestCompletedLevel();
    const nextLevel = getNextLevel();
    const playLabel = hasSleepingGame
      ? 'NEW GAME'
      : (savedLevel > 0 ? `CONTINUE (Lv ${nextLevel})` : 'PLAY');

    createButton(this, { x: centerX, y: nextBtnY, w: 600, h: 112, label: playLabel, color: 0x3498db, hoverColor: 0x2980b9, container, fontSize: '52px', radius: 24, shadowOffset: 4, shadowAlpha: 0.3, glossInset: 8, glossAlpha: 0.12, onClick: () => {
      launchGameScene(this, { level: nextLevel });
    } });
    nextBtnY += 160;

    // ── Levels button ────────────────────────────────────────────────────
    createButton(this, { x: centerX, y: nextBtnY, w: 600, h: 112, label: 'LEVELS', color: 0x9b59b6, hoverColor: 0x8e44ad, container, fontSize: '52px', radius: 24, shadowOffset: 4, shadowAlpha: 0.3, glossInset: 8, glossAlpha: 0.12, onClick: () => {
      fadeToScene(this, () => {
        // Stop the sleeping GameScene if it exists
        if (this.scene.isSleeping('GameScene')) {
          this.scene.stop('GameScene');
        }
        this.scene.start('LevelSelectScene');
      });
    } });

    // ── Instructions ─────────────────────────────────────────────────────
    const instructionsY = hasSleepingGame ? centerY + 540 : centerY + 420;
    container.add(crispText(this.add.text(centerX, instructionsY, 'Tap to send attackers.\nClear all the cubes!', {
      fontFamily: FONT_FAMILY,
      fontSize: '30px',
      color: '#7777aa',
      align: 'center',
    })).setOrigin(0.5));

    // ── Settings gear button (top-right) ─────────────────────────────────
    const settingsOverlay = new PauseOverlay(this, () => {}, {
      title: 'SETTINGS',
      showResume: false,
      showMainMenu: false,
      closeLabel: 'Close',
    });

    createGearButton(this, GAME_WIDTH - 52, 52, () => {
      soundManager.init();
      settingsOverlay.show();
    });

    // ── Developer level picker (compact number entry) ────────────────────
    let devY = instructionsY + 100;
    container.add(crispText(this.add.text(centerX, devY, 'Developer', {
      fontFamily: FONT_FAMILY,
      fontSize: '32px',
      color: '#ff4444',
      fontStyle: 'bold',
    })).setOrigin(0.5));
    devY += 48;

    // Range label
    const rangeLabel = crispText(this.add.text(centerX, devY, `Level 1–${LEVEL_COUNT}`, {
      fontFamily: FONT_FAMILY,
      fontSize: '26px',
      color: '#7777aa',
    })).setOrigin(0.5);
    container.add(rangeLabel);
    devY += 56;

    // HTML input element for level number (works on mobile/Android)
    const inputW = 320;
    const inputH = 88;
    const inputX = centerX - 80; // shifted left to make room for GO button

    // Create a real HTML input element
    const htmlInput = document.createElement('input');
    htmlInput.type = 'number';
    htmlInput.min = '1';
    htmlInput.max = String(LEVEL_COUNT);
    htmlInput.placeholder = '#';
    htmlInput.inputMode = 'numeric';
    htmlInput.pattern = '[0-9]*';
    htmlInput.id = 'dev-level-input';
    htmlInput.style.cssText = `
      position: absolute;
      background: #1e1e3a;
      color: #ffffff;
      border: 4px solid #555577;
      border-radius: 16px;
      font-family: ${FONT_FAMILY};
      font-size: 40px;
      font-weight: bold;
      text-align: center;
      outline: none;
      box-sizing: border-box;
      -moz-appearance: textfield;
      z-index: 10;
      pointer-events: auto;
    `;
    // Hide spinner buttons for number input
    const style = document.createElement('style');
    style.textContent = `
      #dev-level-input::-webkit-outer-spin-button,
      #dev-level-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      #dev-level-input:focus {
        border-color: #3498db;
      }
    `;
    document.head.appendChild(style);

    // Position the input over the canvas
    const positionInput = () => {
      const canvas = this.game.canvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = rect.width / GAME_WIDTH;
      const scaleY = rect.height / getGameHeight();

      // Account for container scroll offset
      const scrollY = container.y;

      const screenX = rect.left + (inputX - inputW / 2) * scaleX;
      const screenY = rect.top + (devY - inputH / 2 + scrollY) * scaleY;
      const screenW = inputW * scaleX;
      const screenH = inputH * scaleY;

      htmlInput.style.left = `${screenX}px`;
      htmlInput.style.top = `${screenY}px`;
      htmlInput.style.width = `${screenW}px`;
      htmlInput.style.height = `${screenH}px`;
      htmlInput.style.fontSize = `${Math.max(14, 40 * scaleY)}px`;
    };

    document.body.appendChild(htmlInput);
    positionInput();

    // Reposition on resize
    const resizeHandler = () => positionInput();
    window.addEventListener('resize', resizeHandler);

    // Track scroll position changes
    const scrollTracker = this.time.addEvent({
      delay: 100,
      callback: positionInput,
      loop: true,
    });

    // Error/feedback text
    const feedbackText = crispText(this.add.text(centerX, devY + 64, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '24px',
      color: '#e74c3c',
    })).setOrigin(0.5).setAlpha(0);
    container.add(feedbackText);

    const showFeedback = (msg: string) => {
      feedbackText.setText(msg).setAlpha(1);
      this.tweens.add({
        targets: feedbackText,
        alpha: 0,
        duration: 2000,
        delay: 1000,
        ease: 'Power2',
      });
      // Shake the input
      htmlInput.style.animation = 'none';
      void htmlInput.offsetHeight; // force reflow
      htmlInput.animate([
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(8px)' },
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(0)' },
      ], { duration: 200 });
    };

    const goToLevel = () => {
      const num = parseInt(htmlInput.value, 10);
      if (isNaN(num) || num < 1 || num > LEVEL_COUNT) {
        showFeedback(`Enter 1–${LEVEL_COUNT}`);
        return;
      }
      launchGameScene(this, { level: num });
    };

    // Handle Enter key on the input
    htmlInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        goToLevel();
      }
    });

    // GO button
    const goX = inputX + inputW / 2 + 100;
    createButton(this, {
      x: goX, y: devY, w: 140, h: 88,
      label: 'GO', color: 0x3498db, hoverColor: 0x2980b9,
      container, fontSize: '40px', radius: 16,
      shadowOffset: 4, shadowAlpha: 0.3, glossInset: 6, glossAlpha: 0.12,
      onClick: goToLevel,
    });

    // Cleanup HTML elements when scene stops
    this.events.once('shutdown', () => {
      htmlInput.remove();
      style.remove();
      window.removeEventListener('resize', resizeHandler);
      scrollTracker.destroy();
    });
    this.events.once('destroy', () => {
      htmlInput.remove();
      style.remove();
      window.removeEventListener('resize', resizeHandler);
      scrollTracker.destroy();
    });

    // Entrance animation for dev section
    [rangeLabel].forEach((el, i) => {
      if ('setAlpha' in el) {
        (el as Phaser.GameObjects.Components.Alpha).setAlpha(0);
      }
      this.tweens.add({
        targets: el,
        alpha: 1,
        y: (el as { y: number }).y,
        duration: 400,
        delay: 200 + i * 60,
        ease: 'Back.easeOut',
      });
    });
    // Fade in the HTML input
    htmlInput.style.opacity = '0';
    htmlInput.style.transition = 'opacity 0.4s ease';
    setTimeout(() => { htmlInput.style.opacity = '1'; }, 300);

    const totalContentHeight = devY + 160;

    // Enable scrolling if content overflows
    enableScrolling(this, container, totalContentHeight);

    // Fade in
    this.cameras.main.fadeIn(300, 0, 0, 0);
  }
}
